/**
 * "há 3 dias", "há 2h", "agora" — tempo decorrido em português, sem
 * biblioteca externa. Usado tanto para "tempo nesse estado" quanto para
 * "último dado sincronizado" (build-prompt §4.1), com prefixos diferentes
 * escolhidos por quem chama.
 */
export function formatElapsed(iso: string | null): string {
  if (!iso) return 'nunca';

  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'agora';

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'há 1 dia';
  return `há ${days} dias`;
}

interface TrendPointLike {
  bucket: string;
  min: number;
  max: number;
  avg: number;
}

/**
 * O catálogo de métricas guarda a unidade canônica de armazenamento (metros,
 * segundos), que é a certa para agregação no banco mas errada para o médico
 * ler num gráfico. Aqui convertemos só na borda de exibição: km e minutos.
 */
export function toTrendDisplayUnit<P extends TrendPointLike>(
  canonicalUnit: string | undefined,
  points: P[],
): { unit: string | undefined; points: P[]; formatValue?: (value: number) => string } {
  if (canonicalUnit === 'm') {
    return {
      unit: 'km',
      points: points.map((p) => ({
        ...p,
        min: p.min / 1000,
        max: p.max / 1000,
        avg: p.avg / 1000,
      })),
      formatValue: (v) => v.toFixed(1).replace('.', ','),
    };
  }

  if (canonicalUnit === 's') {
    return {
      unit: 'min',
      points: points.map((p) => ({ ...p, min: p.min / 60, max: p.max / 60, avg: p.avg / 60 })),
      formatValue: (v) => Math.round(v).toString(),
    };
  }

  return { unit: canonicalUnit, points };
}
